import React, { useState } from "react";
import { FileCode, FileDown, FileText, Loader2 } from "lucide-react";
import { exporter } from "@/lib/api";
import { toast } from "sonner";
import NumaLogo from "@/components/NumaLogo";
import NumaMascot from "@/components/NumaMascot";
import AiBadge from "@/components/AiBadge";

function ExportCard({ format, title, desc, icon: Icon, onClick, loading }) {
  return (
    <button
      data-testid={`export-${format}-btn`}
      onClick={onClick}
      disabled={loading}
      className="w-full text-left bg-surface border border-line rounded-lg p-6 hover:border-line-strong hover:border-line-strong transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed group"
    >
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-md bg-surface flex items-center justify-center flex-shrink-0 shadow-sm">
          {loading ? <Loader2 className="w-6 h-6 text-assistant animate-spin" /> : <Icon className="w-6 h-6 text-assistant" />}
        </div>
        <div className="flex-1">
          <p className="font-serif-display text-xl font-semibold text-ink transition">{title}</p>
          <p className="text-sm text-ink-muted mt-1 leading-relaxed">{desc}</p>
        </div>
        <FileDown className="w-5 h-5 text-ink-muted transition-colors group-hover:text-brand mt-2" />
      </div>
    </button>
  );
}

export default function ExportView({ thesis }) {
  const [busy, setBusy] = useState(null);

  const doExport = async (format) => {
    if (!thesis?.thesis_id) { toast.error("Nessun progetto aperto"); return; }
    setBusy(format);
    try {
      await exporter.download(thesis.thesis_id, format);
      toast.success(`Documento esportato in ${format.toUpperCase()}`);
    } catch {
      toast.error("Errore di esportazione");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="h-screen overflow-y-auto bg-surface numa-fade-in" data-testid="export-view">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 pt-8 lg:pt-14 pb-24">
        <div className="flex items-center gap-3 mb-2">
          <NumaLogo size={8} className="" />
          <p className="text-xs uppercase tracking-widest text-assistant font-medium">Esporta</p>
        </div>
        <h1 className="font-serif-display text-3xl sm:text-4xl font-semibold mt-1 text-ink">Esporta il tuo lavoro</h1>
        <AiBadge mode="none" className="mt-3"
          detail="Il file viene composto dal tuo testo. Nessun modello coinvolto." />
        <p className="mt-2 max-w-3xl text-ink-muted">
          PDF per la consegna, DOCX per continuare in Word. Impaginazione e stili sono conservati.
        </p>

        <div className="mt-8 grid grid-cols-1 gap-4">
          <ExportCard
            format="pdf"
            title="Esporta in PDF"
            desc="Documento pronto per stampa o invio, con capitoli impaginati e contenuto aggiornato."
            icon={FileText}
            onClick={() => doExport("pdf")}
            loading={busy === "pdf"}
          />
          <ExportCard
            format="docx"
            title="Esporta in Word (DOCX)"
            desc="Formato modificabile in Microsoft Word, ideale per consegnare una versione editabile."
            icon={FileCode}
            onClick={() => doExport("docx")}
            loading={busy === "docx"}
          />
        </div>

        <div className="mt-8 bg-surface border border-line rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <NumaLogo size={7} className="" />
            <p className="text-[11px] uppercase tracking-widest text-assistant font-medium">Progetto attivo</p>
          </div>
          <p className="font-serif-display text-xl font-semibold text-ink">{thesis?.title || "Nessun progetto selezionato"}</p>
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-ink-muted">
            <span><strong className="text-ink">{thesis?.chapters?.length || 0}</strong> capitoli</span>
            <span><strong className="text-ink">{thesis?.completion_percentage || 0}%</strong> completata</span>
          </div>
        </div>

        {/* Mascot banner */}
        <div className="mt-6 bg-surface border border-line rounded-md p-5 flex items-center gap-4">
          <div className="hidden sm:block flex-shrink-0">
            <NumaMascot size={64} className="" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-ink">
              <strong>NUMA</strong> ti aiuta a esportare il tuo lavoro nei formati che preferisci.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
